import { createContext, useContext, ReactNode } from 'react';
import { User, UserPermissions, UserContextType } from './UserContext';
import { useUserPermissions } from '../features/User/useUserQueries';

// Create the UserContext
export const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserContextProviderProps {
  children: ReactNode;
}

const defaultPermissions: UserPermissions = {
  isReadOnly: false,
};

export const UserContextProvider = ({ children }: UserContextProviderProps) => {
  const user: User | null = null;

  const { data, isPending, isError, error } = useUserPermissions();

  const permissions: UserPermissions = data
    ? { isReadOnly: data?.readOnly || false }
    : defaultPermissions;

  const contextValue: UserContextType = {
    user,
    permissions,
    loading: isPending,
    error: isError ? ((error as Error)?.message ?? 'Failed to fetch permissions') : null,
  };

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};

// Hook for consuming the context
export const useUserContext = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserContextProvider');
  }
  return context;
};

// Hook for read-only permission check
export const useIsReadOnly = (): boolean => {
  const { permissions } = useUserContext();
  return permissions?.isReadOnly ?? false;
};
